import React, { type ReactNode } from 'react';
import Tooltip, {
  TOOLTIP_PLACEMENT_BOTTOM,
  type TooltipPlacement,
} from '../Tooltip';
import { devWarn } from '../../_internal/devWarn';
import {
  BUTTON_SIZE_MD,
  BUTTON_VARIANT_FILLED,
  type ButtonSize,
  type ButtonVariant,
} from './Button.constants';
import { Root, Spinner } from './Button.styled';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icon shown before the label. Pass any node — Material symbol, react-icons, svg. */
  leadingIcon?: ReactNode;
  /** Icon shown after the label. */
  trailingIcon?: ReactNode;
  /** When true the button fills its container (useful for stacked CTAs). */
  stretch?: boolean;
  /**
   * When true the button is rendered as a circle and only the leading icon
   * is shown. Use this for compact icon-only triggers; for the larger
   * surface-painted icon controls, use `IconButton` instead.
   */
  iconOnly?: boolean;
  /**
   * When true the button shows a spinner in place of the leading icon and
   * disables the click target. Existing children are dimmed but rendered so
   * the layout doesn't shift.
   */
  loading?: boolean;
  /**
   * Optional hover/focus tooltip body. When provided, the button is wrapped
   * in `<Tooltip portal>` so the tip escapes `overflow: hidden` ancestors
   * (e.g. floating toolbars). Mostly useful for icon-only or short-label
   * buttons whose intent benefits from a fuller description on hover.
   */
  tooltip?: ReactNode;
  /**
   * Tooltip placement relative to the button. Defaults to `bottom`; flip to
   * `top` for buttons sitting at the bottom of a floating panel.
   */
  tooltipPlacement?: TooltipPlacement;
  /** Where the tooltip renders; defaults to `document.body`. Pass a node inside a shadow root to keep its styles. */
  tooltipPortalContainer?: Element | null;
  children?: ReactNode;
  dataTestId?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = BUTTON_VARIANT_FILLED,
    size = BUTTON_SIZE_MD,
    leadingIcon,
    trailingIcon,
    stretch = false,
    iconOnly = false,
    loading = false,
    tooltip,
    tooltipPlacement = TOOLTIP_PLACEMENT_BOTTOM,
    tooltipPortalContainer,
    disabled,
    type = 'button',
    children,
    dataTestId = 'button',
    ...rest
  },
  ref,
) {
  if (iconOnly && !rest['aria-label'] && !rest['aria-labelledby']) {
    devWarn(
      'Button: when `iconOnly` is true the visible label is hidden — pass `aria-label` (or `aria-labelledby`) so screen reader users can still identify the control.',
    );
  }

  const isDisabled = disabled || loading;
  const showLeading = leadingIcon && !loading;

  const button = (
    <Root
      ref={ref}
      type={type}
      $variant={variant}
      $size={size}
      $stretch={stretch}
      $iconOnly={iconOnly}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-testid={dataTestId}
      {...rest}
    >
      {loading && <Spinner aria-hidden="true" data-testid={`${dataTestId}__spinner`} />}
      {showLeading && (
        <span aria-hidden="true" data-testid={`${dataTestId}__leading`}>
          {leadingIcon}
        </span>
      )}
      {!iconOnly && children}
      {!iconOnly && trailingIcon && (
        <span aria-hidden="true" data-testid={`${dataTestId}__trailing`}>
          {trailingIcon}
        </span>
      )}
    </Root>
  );

  // Treat every "renders nothing" ReactNode as "no tooltip" so callers
  // can safely pass `condition && 'msg'` (which evaluates to `false` when
  // off) without getting an empty/ghost popup. We explicitly preserve
  // `0` because that's a legitimate, render-able ReactNode body.
  if (
    tooltip === undefined ||
    tooltip === null ||
    tooltip === false ||
    tooltip === ''
  ) {
    return button;
  }

  return (
    <Tooltip
      content={tooltip}
      placement={tooltipPlacement}
      portal
      portalContainer={tooltipPortalContainer}
      dataTestId={`${dataTestId}__tooltip`}
    >
      {button}
    </Tooltip>
  );
});

export default Button;
